import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Label } from '@/app/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/components/ui/alert-dialog';
import { SignaturePad } from '@/components/safety/signature-pad';
import {
  usePortalApproveBid,
  usePortalBid,
  usePortalRejectBid,
  usePortalValidate,
} from '@/hooks/use-projects';
import { formatMoney } from '@/lib/currency';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-600',
};

export function PortalBidDetailPage() {
  const { token, bidId } = useParams<{ token: string; bidId: string }>();
  const parsedBidId = parseInt(bidId ?? '0', 10);
  const { data: bid, isLoading } = usePortalBid(token ?? '', parsedBidId);
  const { data: session } = usePortalValidate(token ?? '');
  const approveMutation = usePortalApproveBid(token ?? '');
  const rejectMutation = usePortalRejectBid(token ?? '');
  const [rejectReason, setRejectReason] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);

  const canRespond = bid?.status === 'sent' || bid?.status === 'under_review';
  const canApprove = canRespond && !!signerName.trim() && !!signatureDataUrl;

  const handleSignatureAccept = (dataUrl: string) => {
    setSignatureDataUrl(dataUrl);
  };

  const handleSignatureClear = () => {
    setSignatureDataUrl(null);
  };

  const handleApprove = () => {
    if (!signatureDataUrl || !signerName.trim()) return;
    approveMutation.mutate({
      bidId: parsedBidId,
      signature_png_base64: signatureDataUrl,
      signer_name: signerName.trim(),
    });
  };

  const handleReject = () => {
    rejectMutation.mutate({ bidId: parsedBidId, reason: rejectReason });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!bid) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Bid Not Found</h1>
          <p className="text-gray-600">This bid could not be found.</p>
        </div>
      </div>
    );
  }

  const currency = bid.currency ?? 'USD';

  return (
    <>
      <nav className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/portal/${token}`}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Portal
            </Link>
          </Button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{bid.title}</h1>
              <Badge className={statusColors[bid.status] ?? ''}>
                {bid.status.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-gray-600 mt-1">{bid.bid_number}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Total Amount</p>
            <p className="text-3xl font-bold text-gray-900">{formatMoney(bid.total, currency)}</p>
          </div>
        </div>

        {/* Bid Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bid Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block">Bid Number</span>
                <span className="font-medium">{bid.bid_number}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Created</span>
                <span className="font-medium">
                  {formatDateTimeInTimeZone(
                    bid.created_at,
                    session?.organization_time_zone ?? 'UTC',
                    { dateStyle: 'medium' }
                  )}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Valid Until</span>
                <span className="font-medium">
                  {bid.valid_until
                    ? formatDateOnly(bid.valid_until)
                    : 'Not set'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Status</span>
                <span className="font-medium capitalize">{bid.status.replace('_', ' ')}</span>
              </div>
            </div>
            {bid.description && (
              <div className="mt-4">
                <span className="text-sm text-gray-500 block mb-1">Description</span>
                <p className="text-sm whitespace-pre-wrap">{bid.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Line Items</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bid.line_items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">{formatMoney(item.unit_price, currency)}</TableCell>
                    <TableCell className="text-right font-medium">{formatMoney(item.total, currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-end gap-2 text-sm">
              <div className="flex gap-8">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium w-32 text-right">{formatMoney(bid.subtotal, currency)}</span>
              </div>
              {parseFloat(bid.tax_amount) > 0 && (
                <div className="flex gap-8">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-medium w-32 text-right">{formatMoney(bid.tax_amount, currency)}</span>
                </div>
              )}
              <div className="flex gap-8 text-lg font-bold border-t pt-2 mt-1">
                <span>Total:</span>
                <span className="w-32 text-right">{formatMoney(bid.total, currency)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* E-Signature & Action Buttons */}
        {canRespond && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Approve or Reject</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Signer name + signature pad for approval */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="signer-name">Your Full Name</Label>
                  <Input
                    id="signer-name"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Enter your full legal name"
                    className="mt-1 max-w-sm"
                  />
                </div>
                <div>
                  <Label>Signature</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Draw your signature below to approve this bid.
                  </p>
                  <SignaturePad
                    onAccept={handleSignatureAccept}
                    onClear={handleSignatureClear}
                    width={460}
                    height={150}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="lg"
                      className="bg-green-600 hover:bg-green-700"
                      disabled={!canApprove}
                    >
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Approve Bid
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Approve This Bid?</AlertDialogTitle>
                      <AlertDialogDescription>
                        By approving this bid for {formatMoney(bid.total, currency)}, you authorize the work to proceed.
                        Your e-signature will be recorded. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleApprove}
                        disabled={approveMutation.isPending}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {approveMutation.isPending ? 'Approving...' : 'Confirm Approval'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="lg" variant="destructive">
                      <XCircle className="h-5 w-5 mr-2" />
                      Reject Bid
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reject This Bid?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Please provide a reason for rejecting this bid.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-4">
                      <Label>Reason for Rejection</Label>
                      <Textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Please explain why you are rejecting this bid..."
                        rows={3}
                      />
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleReject}
                        disabled={rejectMutation.isPending}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {rejectMutation.isPending ? 'Rejecting...' : 'Confirm Rejection'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        )}

        {bid.status === 'accepted' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="font-medium text-green-800">This bid has been approved</p>
          </div>
        )}

        {bid.status === 'rejected' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
            <p className="font-medium text-red-800">This bid has been rejected</p>
          </div>
        )}
      </main>
    </>
  );
}
